import { Warning } from "@phosphor-icons/react"

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/reusable/Accordion"
import { api } from "~/utils/api"

import { Card, CardContent } from "../reusable/Card"
import LoadingOverlay from "../reusable/LoadingOverlay"
import { StatusTabEmpty, StatusTabLoading } from "./StatusTabStates"
import { useAuth0 } from "@auth0/auth0-react"
import { useUser } from "~/providers/userProvider"

interface OrgViewsProps {
  orgId: string
}

export const OrgViewsTab: React.FC<OrgViewsProps> = ({ orgId }) => {
  const { user, isAuthenticated, isLoading } = useAuth0()
  const { permissions } = useUser()

  const {
    data: views,
    isLoading: viewsLoading,
    error: viewsError,
  } = api.bigQuery.views.useQuery(
    { orgId },
    // An org with no dataset will never grow one on retry; anything else keeps the default 3.
    { retry: (count, error) => error.data?.code !== "NOT_FOUND" && count < 3 },
  )

  if (!user && isLoading) {
    return <LoadingOverlay />
  }

  if (!isAuthenticated) {
    return <div className="bg-subtle p-4">Not authenticated</div>
  }

  if (!permissions.isStaff) {
    return <div className="bg-subtle p-4">User not allowed!</div>
  }

  if (viewsLoading) {
    return <StatusTabLoading />
  }

  if (viewsError) {
    return <StatusTabEmpty message={viewsError.message} icon={Warning} />
  }

  if (!views?.length) {
    return <StatusTabEmpty message="No views found" />
  }

  return (
    <div className="flex flex-col gap-4 w-full">
      {views?.map((view: any) => (
        <Card key={view.tableName} className="w-full">
          <CardContent className="p-0">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1" className="border-0">
                <AccordionTrigger className="px-4 py-3">
                  <span className="text-md">{view.tableName}</span>
                </AccordionTrigger>
                <AccordionContent className="px-4 py-3">
                  <div className="rounded-lg bg-subtle border border-base p-4 overflow-x-auto">
                    <pre className="text-sm font-mono whitespace-pre-wrap break-words">
                      {view.query}
                    </pre>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
